/**
 * The "Documents" card in the Research browse grid — document-level hits
 * (filename / title match). Kept separate from the Mentions card because
 * document rows surface different metadata than chunk snippets.
 *
 * Visual rhythm matches `EntityGroupCard` (icon block + label + count +
 * chevron header, divided body rows) so the grid reads as one system.
 */
import { ChevronRight, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import type { DocumentHit } from '@/services/api/case-search'
import { HighlightedText } from './HighlightedText'

interface Props {
  caseId: string
  documents: DocumentHit[]
  query?: string
}

export function DocumentsCard({ caseId, documents, query }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  if (documents.length === 0) return null

  return (
    <Card className="group/card flex flex-col overflow-hidden p-0 transition-colors hover:border-border/80">
      <div className="flex items-center gap-3 border-b border-border/60 px-3.5 py-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <FileText className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {t('research.documents.label')}
        </span>
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          {documents.length}
        </span>
      </div>
      <ul className="divide-y divide-border/40">
        {documents.map(hit => (
          <li key={hit.document_id} className="cursor-pointer transition-colors hover:bg-muted/30">
            <button
              type="button"
              onClick={() => navigate(`/cases/${caseId}/documents?doc=${hit.document_id}`)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left md:py-1.5"
            >
              <FileText className="size-3 shrink-0 text-muted-foreground/70" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-foreground">
                  <HighlightedText
                    text={hit.name || hit.filename || hit.document_id}
                    query={query}
                  />
                </div>
                {hit.file_type && (
                  <div className="truncate font-mono text-3xs uppercase tracking-wider text-muted-foreground/70">
                    {hit.file_type}
                  </div>
                )}
              </div>
              <ChevronRight className="size-3 shrink-0 text-muted-foreground/30 transition-colors group-hover/card:text-muted-foreground/60" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}
